<template>
  <div v-show="orderDetails.order.number">
    <vs-card class="p-4">
      <div slot="header">
        <h3 class="mb-2 font-bold">Obrigado por sua Compra!</h3>
      </div>
      <div>
        <div class="mb-4">
          <div><strong class="mb-1 text-lg">Número do Pedido</strong></div>
          <div class="text-lg">{{ orderDetails.order.number || '-' }}</div>
        </div>
        <div
          role="alert"
          class="alert alert-dismissible text-center-pay"
          :class="{
            'alert-warning': orderDetails.order.status != 'Pago',
            'alert-success': orderDetails.order.status == 'Pago'
          }"
        >
          <div><strong class="mb-1 text-lg">Situação do Pagamento</strong></div>
          <div class="text-lg">{{ orderDetails.order.status || '-' }}</div>
        </div>

        <div
          role="alert"
          class="alert alert-wraning alert-dismissible text-center-pay"
          v-show="orderDetails.order.status === 'Aguardando Pagamento'"
        >
          <div class="text-lg mt-4">
            Às vezes pode levar alguns minutos até recebermos a notificação do pagamento.<br />Se você não receber um
            e-mail confirmando a compra em 24 horas, entre em contato com nosso suporte.
          </div>
        </div>
      </div>

      <div class="mt-6">
        <a href="/dispositivo">
          <vs-button type="relief">Acessar Painel</vs-button>
        </a>
      </div>
    </vs-card>

    <!-- Boleto -->
    <vs-card class="p-4" v-show="orderDetails.order.paymentMethod == 'Boleto' && orderDetails.order.status != 'Pago'">
      <div slot="header">
        <h4 class="font-bold">Instruções para Pagamento</h4>

        <div class="text-lg my-4">
          Clique abaixo para imprimir o boleto e efetuar o pagamento. Após o pagamento do boleto, pode levar até 3
          dias úteis para liberar o acesso. A liberação é automática pelo e-mail cadastrado no boleto.
        </div>

        <!-- Starkbank: link direto para o PDF do boleto -->
        <div v-if="boletoPdfUrl" class="mt-2">
          <a :href="boletoPdfUrl" target="_blank" rel="noopener noreferrer">
            <vs-button type="relief">Imprimir Boleto</vs-button>
          </a>
        </div>

        <!-- Itaú (legado): formulário Shopline -->
        <template v-else-if="orderDetails.order.chavePedido">
          <form
            id="shopline"
            method="post"
            action="https://shopline.itau.com.br/shopline/impressao.aspx"
            target="shopline"
          >
            <input type="hidden" name="DC" v-model="orderDetails.order.chavePedido" />

            <div class="mt-2">
              <vs-button type="relief" button="submit">Imprimir Boleto</vs-button>
            </div>
          </form>
        </template>

        <!-- Fallback: sem PDF nem chave Itaú -->
        <div v-else class="mt-2 text-lg">
          O link do boleto foi enviado para o seu e-mail. Verifique a caixa de entrada e o spam. Caso não encontre,
          entre em contato com o suporte informando o número do pedido <strong>{{ orderDetails.order.number }}</strong>.
        </div>
      </div>
    </vs-card>

    <!-- Pix -->
    <vs-card class="p-4" v-show="orderDetails.order.paymentMethod === 'Pix'">
      <div slot="header">
        <div v-if="orderDetails.pix.qrcode">
          <h4 class="mt-6 font-bold">Faça o pagamento usando o QR code abaixo:</h4>

          <img v-if="orderDetails.pix.qrcode" :src="orderDetails.pix.qrcode" width="200px" height="200px" />

          <div>
            <div>Você também pode pagar usando Pix Copia e Cola abaixo:</div>
            <vs-button
              class="mt-4"
              type="relief"
              v-clipboard:copy="orderDetails.pix.code"
              v-clipboard:success="onClipboardSuccess"
              v-clipboard:error="onClipboardError"
              >Copiar QR Code</vs-button
            >

            <div class="mt-4 break-all text-success">{{ orderDetails.pix.code }}</div>
          </div>

          <div class="mt-4 text-lg">
            Após o pagamento <router-link to="/dispositivo">clique aqui</router-link> para acessar o painel.<br />
            Na maioria das vezes a liberação é imediata, porém pode ser que seja necessário aguardar alguns minutos
            devido ao processo de liberação do pagamento pelo seu banco.
          </div>
        </div>

        <div v-else>
          <h4 class="mt-6 font-bold">Chave Pix</h4>

          <h5>{{ appChavePix }}</h5>

          <div class="mt-4 text-lg">
            Após o pagamento envie o comprovante para o e-mail <strong>{{ appEmailVendas }}</strong> informando o número
            do pedido <strong>{{ orderDetails.order.number }}</strong
            >.
          </div>

          <div class="mt-4 text-lg">Prazo para envio da licença:</div>
          <div>Enviamos a licença ou liberamos a conta somente após a confirmação do Pix.</div>
        </div>
      </div>
    </vs-card>

    <!-- Transferência -->
    <vs-card class="p-4" v-show="orderDetails.order.paymentMethod == 'Transferência'">
      <div slot="header">
        <h4 class="font-bold">Instruções para Transferência</h4>
        <h4 class="mt-6 font-bold">Chave Pix</h4>
        <h5>{{ appChavePix }}</h5>

        <h4 class="mt-6 font-bold">TED/DOC/Depósito Bancário</h4>
        <div class="text-lg">
          <div>{{ appTransferenciaBanco }}</div>
          <div><strong>Agência:</strong> {{ appTransferenciaAgencia }}</div>
          <div><strong>Conta Corrente:</strong> {{ appTransferenciaConta }}</div>
          <div><strong>Nome:</strong> {{ appTransferenciaNome }}</div>
          <div><strong>CNPJ:</strong> {{ appTransferenciaCnpj }}</div>
        </div>

        <div class="mt-4 text-lg">
          Após o pagamento envie o comprovante para o e-mail <strong>{{ appEmailVendas }}</strong> informando o número
          do pedido <strong>{{ orderDetails.order.number }}</strong
          >.
        </div>

        <div class="mt-4 text-lg">Prazo para envio da licença:</div>
        <div>Enviamos a licença ou liberamos a conta somente após a confirmação da transferência/depósito.</div>
      </div>
    </vs-card>

    <!-- Licenças -->
    <vs-card class="p-4" v-show="orderDetails.licences && orderDetails.licences.length">
      <div slot="header">
        <h4 class="font-bold">Licenças</h4>
        <div class="mt-4 text-lg">
          <vs-table stripe :data="orderDetails.licences">
            <template slot="thead">
              <vs-th>Programa</vs-th>
              <vs-th>Licença</vs-th>
              <vs-th>Validade</vs-th>
            </template>

            <template slot-scope="{ data }">
              <vs-tr :key="index" v-for="(tr, index) in data">
                <vs-td>
                  {{ data[index].product.name }}
                </vs-td>
                <vs-td>
                  {{ data[index].licencekey || 'Ativação via Painel' }}
                </vs-td>
                <vs-td v-if="data[index].date_valid_until">
                  {{ data[index].date_valid_until | formatDate }}
                </vs-td>
                <vs-td v-else> Após Ativação</vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </div>
      </div>
    </vs-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      appEmailVendas: process.env.VUE_APP_EMAIL_VENDAS,
      appChavePix: process.env.VUE_APP_CHAVE_PIX,
      appTransferenciaBanco: process.env.VUE_APP_TRANSFERENCIA_BANCO,
      appTransferenciaAgencia: process.env.VUE_APP_TRANSFERENCIA_AGENCIA,
      appTransferenciaConta: process.env.VUE_APP_TRANSFERENCIA_CONTA,
      appTransferenciaNome: process.env.VUE_APP_TRANSFERENCIA_NOME,
      appTransferenciaCnpj: process.env.VUE_APP_TRANSFERENCIA_CNPJ
    };
  },
  props: {
    orderDetails: {
      type: Object,
      required: true
    }
  },
  computed: {
    boletoPdfUrl() {
      const order = (this.orderDetails && this.orderDetails.order) ? this.orderDetails.order : {};
      return order.boletoPdf || order.boleto_pdf || '';
    }
  },
  methods: {
    onClipboardSuccess: function() {
      this.$vs.notify({
        text: 'QR Code copiado com sucesso.',
        color: 'success'
      });
    },
    onClipboardError: function() {
      this.$vs.notify({
        text: 'Ocorreu um erro ao copiar o código. Ao invés disso, copie o texto que está na tela.',
        color: 'danger'
      });
    }
  }
};
</script>
